// /groups/:groupId/members — the roster, and what the group is.
//
// This used to BE the group's landing page. It moved here when the feed became
// the group's home (docs/plan-feed-as-home.md): landing on a group should put a
// member in its conversation, not in a list of people. The group's description
// came along with the roster, so this page is now "about this group" as well.
//
// Every management control is gated on the caller's own role, which the server
// sends back on the group payload. The server enforces the same rules; hiding a
// button is a convenience, never the guard.

import {
  ApiClientError,
  fetchGroup,
  listInviteCodes,
  listMembers,
  removeMember,
  updateMemberRole,
} from '../api/client.js';
import { roleSelect } from './roleSelect.js';
import { codesSection, joinRequestsSection } from './groupManage.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import {
  canInvite,
  type InviteCode,
  formatMemberCount,
  leaveGroupWarning,
  GroupRole,
  type GroupDetail,
  type GroupMember,
} from '@msc/shared';
import { groupRunsFasts, groupShowsLeaderboard, groupSubnav } from './groupNav.js';
import { inviteSection } from './inviteMemberForm.js';
import { groupInfoPanel } from './groupInfoPanel.js';

export const groupView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  let group: GroupDetail;
  let members: GroupMember[];
  let codes: InviteCode[] = [];
  try {
    [group, members] = await Promise.all([fetchGroup(groupId), listMembers(groupId)]);
    // Admin-only data, fetched after the role is known. A failure here must not
    // take the roster down with it: the codes card simply starts empty.
    if (group.myRole >= GroupRole.ADMIN) {
      codes = await listInviteCodes(groupId).catch(() => []);
    }
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    const msg =
      err instanceof ApiClientError && err.status === 403
        ? 'You are not a member of this group.'
        : 'Could not load this group.';
    main.replaceChildren(el('p', { class: 'form-error' }, msg), backLink());
    return;
  }

  const canManage = group.myRole >= GroupRole.ADMIN;

  const memberCount = el(
    'p',
    { class: 'muted' },
    `You are ${group.myRoleLabel} · ${formatMemberCount(group.memberCount, group.memberNoun)}`,
  );
  const header = el('div', { class: 'page-head' }, [el('h1', {}, group.name), memberCount]);

  // Held so a successful invite can swap it. `replaceWith` detaches the old
  // node, so the handle has to be reassigned or the NEXT refresh writes into
  // something that is no longer on the page.
  let roster = rosterTable(group, members, canManage);
  let panel = groupInfoPanel(group, members);

  /**
   * Re-read the roster after an invite. The SPA equivalent of PHP's
   * post-redirect-get, except it refreshes only what changed: re-rendering the
   * page would destroy the invite form and its success message along with it.
   */
  async function refreshRoster(): Promise<void> {
    const [fresh, freshMembers] = await Promise.all([fetchGroup(groupId), listMembers(groupId)]);
    const next = rosterTable(fresh, freshMembers, fresh.myRole >= GroupRole.ADMIN);
    roster.replaceWith(next);
    roster = next;
    memberCount.textContent =
      `You are ${fresh.myRoleLabel} · ${formatMemberCount(fresh.memberCount, fresh.memberNoun)}`;
    // The panel carries the count and the helper list, so a roster change moves
    // it too. Reassigned for the same reason `roster` is: replaceWith detaches.
    const nextPanel = groupInfoPanel(fresh, freshMembers);
    panel.replaceWith(nextPanel);
    panel = nextPanel;
  }

  main.replaceChildren(
    backLink(),
    // The section tabs sit directly under the back link, above the group's
    // title and info panel, so they are reachable without scrolling past them.
    groupSubnav(groupId, 'members', canManage, groupRunsFasts(group.allowedEventTypes), groupShowsLeaderboard(group.allowedEventTypes)),
    header,
    // Impossible to miss, and above the content rather than below it: every
    // write on this page will be refused by the server, and the member deserves
    // to know that before they try one.
    ...(group.isSuspended
      ? [
          el(
            'p',
            { class: 'notice notice-suspended' },
            'This group is suspended by a site administrator. You can still read ' +
              'everything here, but nobody can post, upload, RSVP, or create events ' +
              'until it is restored.',
          ),
        ]
      : []),
    panel,
    group.description ? el('p', {}, group.description) : el('p', { class: 'muted' }, 'No description yet.'),
    el('h2', {}, 'Members'),
    roster,
    // Invites live here as well as on Manage. Manage is where you configure a
    // group; Members is where you go when you are thinking about PEOPLE, and
    // that is where it gets looked for. Same component, one definition.
    //
    // `canInvite()` is the server's own gate, so this is never rendered to
    // someone who would then be refused -- and under `members_open` it is a
    // plain member's only route to it, since Manage is Admin-only.
    ...(canInvite(group.myRole, group.invitePermission)
      ? [inviteSection(group, { onInvited: refreshRoster })]
      : []),
    // People management lives here for owners and admins (moved off Manage
    // 2026-10-20): join requests, then invite codes. Roles are the dropdowns in
    // the roster above.
    ...(canManage ? [joinRequestsSection(group), codesSection(group, codes)] : []),
    leaveSection(group),
  );
};

function backLink(): HTMLElement {
  return el('p', {}, [el('a', { href: '/groups' }, 'Back to your groups')]);
}

function rosterTable(
  group: GroupDetail,
  members: GroupMember[],
  canManage: boolean,
): HTMLElement {
  const rows = members.map((m) => {
    const cells = [
      // The name links to the member's profile — this roster is how the profile
      // page is discovered, since nothing else in the app points at a person.
      // An invite that has not been accepted yet has no profile to show, so
      // those stay plain text rather than linking to a 404.
      el('td', {}, [
        el('span', { class: 'member-name' }, [
          memberAvatar(m),
          m.accountActive
            ? el('a', { href: `/users/${m.userId}` }, m.fullName || m.email || `User ${m.userId}`)
            : m.fullName || m.email || `User ${m.userId}`,
        ]),
      ]),
      // An owner or admin changes a role right here; everyone else reads it. The
      // Owner row is never editable (it moves only by transfer, on Manage).
      el('td', {}, canManage && m.role < GroupRole.OWNER ? [roleControl(group, m)] : m.roleLabel),
      // Empty when the viewer is not allowed to see it: a member sees a phone
      // only when it was made public, while an admin always does.
      el('td', {}, m.phone || ''),
      el('td', {}, m.accountActive ? '' : 'Invite pending'),
    ];
    if (canManage) {
      cells.push(el('td', {}, m.role >= GroupRole.OWNER ? '' : removeButton(group.groupId, m)));
    }
    return el('tr', {}, cells);
  });

  const head = ['Name', 'Role', 'Phone', 'Status'];
  if (canManage) head.push('');

  return el('div', { class: 'table-wrap' }, [
    el('table', { class: 'table' }, [
      el('thead', {}, [el('tr', {}, head.map((h) => el('th', {}, h)))]),
      el('tbody', {}, rows),
    ]),
  ]);
}

/** The role dropdown for one member, saving on change. Mirrors the Manage page's. */
function roleControl(group: GroupDetail, m: GroupMember): HTMLElement {
  const select = roleSelect(`role-${m.userId}`, GroupRole.ADMIN, m.role);
  const status = el('span', { class: 'muted small' }, '');
  select.setAttribute('aria-label', `Role for ${m.fullName || m.email || m.userId}`);
  select.addEventListener('change', async () => {
    select.setAttribute('disabled', 'disabled');
    status.textContent = 'Saving';
    try {
      const result = await updateMemberRole(group.groupId, m.userId, Number(select.value));
      status.textContent = result.changed ? `Now ${result.roleLabel}` : 'No change';
    } catch (err) {
      status.textContent =
        err instanceof ApiClientError ? err.message : 'Could not change that role.';
      select.value = String(m.role);
    } finally {
      select.removeAttribute('disabled');
    }
  });
  // A table cell squeezes a width:100% select to a sliver on a phone (it showed
  // as a clipped "M"), so the control carries its own floor.
  select.style.width = 'auto';
  select.style.minWidth = '9rem';
  return el('span', { class: 'role-control' }, [select, ' ', status]);
}

/**
 * The member's photo, or their initials in the same circle. Decorative: the
 * name sits right beside it, so alt text would only say it twice.
 */
function memberAvatar(m: GroupMember): HTMLElement {
  if (m.avatarThumbUrl) {
    return el('img', { class: 'member-avatar', src: m.avatarThumbUrl, alt: '', loading: 'lazy' });
  }
  const letters =
    `${m.firstName.trim().charAt(0)}${m.lastName.trim().charAt(0)}`.toUpperCase() ||
    (m.email || '?').charAt(0).toUpperCase();
  return el('span', { class: 'member-avatar member-avatar-empty', 'aria-hidden': 'true' }, letters);
}

function removeButton(groupId: number, member: GroupMember): HTMLElement {
  const btn = el('button', { class: 'btn btn-link btn-danger' }, 'Remove');
  btn.addEventListener('click', async () => {
    const who = member.fullName || member.email || `user ${member.userId}`;
    if (!window.confirm(`Remove ${who} from this group?`)) return;
    btn.setAttribute('disabled', 'disabled');
    try {
      await removeMember(groupId, member.userId);
      navigate(`/groups/${groupId}/members`, true);
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not remove that member.');
      btn.removeAttribute('disabled');
    }
  });
  return btn;
}

/**
 * The owner is deliberately given no leave control: leaving would orphan the
 * group. The server returns OWNER_PROTECTED if anyone tries anyway.
 */
function leaveSection(group: GroupDetail): HTMLElement {
  if (group.myRole >= GroupRole.OWNER) {
    return el(
      'p',
      { class: 'muted' },
      'You own this group. Transfer ownership before you can leave it.',
    );
  }

  const btn = el('button', { class: 'btn btn-link btn-danger' }, 'Leave this group');
  btn.addEventListener('click', async () => {
    if (!window.confirm(leaveGroupWarning(group.name, group.visibility))) return;
    btn.setAttribute('disabled', 'disabled');
    try {
      const me = await import('../api/client.js').then((m) => m.fetchMe());
      await removeMember(group.groupId, me.userId);
      navigate('/groups', true);
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not leave the group.');
      btn.removeAttribute('disabled');
    }
  });
  return el('section', { class: 'card' }, [btn]);
}
